import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useSEO from "@/hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignupPage() {
  useSEO({ title: "Create Account", description: "Join CardMyDog and save your AI-generated sports trading cards.", path: "/signup" });
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) navigate("/account", { replace: true });
  }, [user, navigate]);

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please fill in all fields"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      const resp = await axios.post(`${API}/auth/register`, { email, password, full_name: name || null });
      login(resp.data.user, resp.data.token, resp.data.session_token);
      toast.success("Account created!");
      navigate("/account");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-16 flex items-center justify-center" data-testid="signup-page">
      <div className="max-w-sm w-full mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl uppercase tracking-tight text-[var(--color-text-dark)] mb-2">
            Create account
          </h1>
          <p className="font-body text-sm text-[var(--color-text-mid)]">Join Card My Dog and save your cards</p>
        </div>

        <div className="bg-[var(--color-white)] rounded-[12px] border border-[var(--color-border)] p-6">
          <button
            onClick={handleGoogleSignup}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-[8px] border border-[var(--color-border)] bg-[var(--color-cream)] hover:border-[var(--color-gold)] transition-colors font-body text-sm text-[var(--color-text-dark)] mb-5"
            data-testid="google-signup-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-[var(--color-border)]" />
            <span className="font-body text-xs text-[var(--color-text-light)]">or</span>
            <div className="flex-1 h-px bg-[var(--color-border)]" />
          </div>

          <form onSubmit={handleEmailSignup} className="space-y-4">
            <div>
              <Label className="font-body text-sm text-[var(--color-text-dark)] mb-1.5 block">Full name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="rounded-[8px]" data-testid="signup-name-input" />
            </div>
            <div>
              <Label className="font-body text-sm text-[var(--color-text-dark)] mb-1.5 block">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="rounded-[8px]" data-testid="signup-email-input" />
            </div>
            <div>
              <Label className="font-body text-sm text-[var(--color-text-dark)] mb-1.5 block">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" className="rounded-[8px]" data-testid="signup-password-input" />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full font-heading tracking-widest uppercase py-3 rounded-[8px] transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-gold)", color: "var(--color-navy)" }}
              data-testid="signup-submit-btn"
            >
              {loading ? "Creating account..." : "Create Account"}
            </button>
          </form>
        </div>

        <p className="text-center mt-6 font-body text-sm text-[var(--color-text-mid)]">
          Already have an account?{" "}
          <Link to="/login" className="font-medium" style={{ color: "var(--color-gold)" }} data-testid="goto-login">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
